import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import "./ProductDetail.css";

const BASE_URL = "https://dashboard.jyotirvani.com";

const getCartKey = () => {
  const user = JSON.parse(localStorage.getItem("user") || "null");
  return user?.email ? `cart_${user.email}` : "cart_guest";
};

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [product, setProduct] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  const [selectedVariant, setSelectedVariant] = useState(null);
  const [selectedImage, setSelectedImage] = useState("");

  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [errors, setErrors] = useState({});
  const [isPaying, setIsPaying] = useState(false);
  const [cartMessage, setCartMessage] = useState("");

  useEffect(() => {
    setLoading(true);

    axios
      .post(`${BASE_URL}/api/productdetails`, { productid: id })
      .then((res) => {
        const data = res.data?.data;

        if (!data) {
          setProduct(null);
          return;
        }

        const mainImage = data.image
          ? `${BASE_URL}/uploads/productImage/${data.image}`
          : "";

        const variants = Array.isArray(data.variants)
          ? data.variants
              .slice(0, 10)
              .sort((a, b) => Number(a.position || 0) - Number(b.position || 0))
              .map((item) => ({
                ...item,
                size: item.size || item.variant_size || "",
                unit: item.unit || item.variant_unit || "",
                main_price: item.main_price ? Number(item.main_price) : 0,
                price: item.price ? Number(item.price) : 0,
                stock_status: item.stock_status || "in_stock",
                imageUrl: item.image
                  ? `${BASE_URL}/uploads/productVariantImage/${item.image}`
                  : mainImage,
              }))
          : [];

        const formattedProduct = {
          ...data,
          image: mainImage,
          price: data.price ? Number(data.price) : 0,
          stock_status: data.stock_status || "in_stock",
          variants,
        };

        setProduct(formattedProduct);

        const firstVariant = variants[0] || {
          id: "main",
          size: "",
          unit: "",
          main_price: formattedProduct.price,
          price: formattedProduct.price,
          stock_status: formattedProduct.stock_status,
          imageUrl: mainImage,
        };

        setSelectedVariant(firstVariant);
        setSelectedImage(firstVariant.imageUrl || mainImage);
      })
      .catch(() => setProduct(null))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    axios
      .get(`${BASE_URL}/api/fetchproduct`)
      .then((res) => {
        const list = Array.isArray(res.data?.data) ? res.data.data : [];

        const formatted = list.map((item) => ({
          ...item,
          image: item.image
            ? `${BASE_URL}/uploads/productImage/${item.image}`
            : "",
        }));

        setRelatedProducts(formatted);
      })
      .catch(() => setRelatedProducts([]));
  }, []);

  const activePrice = selectedVariant?.price || Number(product?.price || 0);
  const activeMainPrice =
    selectedVariant?.main_price || Number(product?.price || 0);

  const isOutOfStock =
    product?.stock_status === "out_of_stock" ||
    selectedVariant?.stock_status === "out_of_stock";

  const addProductToCart = (goToCart = false) => {
    if (isOutOfStock) {
      setCartMessage("Selected option is out of stock");
      setTimeout(() => setCartMessage(""), 2500);
      return;
    }

    try {
      const cartKey = getCartKey();
      const existingCart = JSON.parse(localStorage.getItem(cartKey)) || [];

      const imageForCart = selectedImage || product.image;
      const variantId = selectedVariant?.id || "main";

      const existingItem = existingCart.find(
        (item) =>
          String(item.id) === String(product.id) &&
          String(item.variant_id) === String(variantId),
      );

      if (existingItem) {
        existingItem.quantity = Number(existingItem.quantity || 1) + 1;
      } else {
        existingCart.push({
          id: product.id,
          variant_id: variantId,
          name: product.name,
          category: product.category,
          price: activePrice,
          main_price: activeMainPrice,
          selected_price: activePrice,
          size: selectedVariant?.size || "",
          unit: selectedVariant?.unit || "",
          image: imageForCart,
          stock_status: selectedVariant?.stock_status || product.stock_status,
          quantity: 1,
        });
      }

      localStorage.setItem(cartKey, JSON.stringify(existingCart));
      window.dispatchEvent(new Event("cartUpdated"));

      if (goToCart) {
        navigate("/cart");
      } else {
        setCartMessage(`Added to cart - ₹${activePrice}`);
        setTimeout(() => setCartMessage(""), 2500);
      }
    } catch (error) {
      console.error("Cart Error:", error);
      setCartMessage("Error adding to cart");
      setTimeout(() => setCartMessage(""), 2500);
    }
  };

  const handlePayClick = () => {
    const isLoggedIn = localStorage.getItem("isLoggedIn") === "true";

    if (!isLoggedIn) {
      navigate("/login", { state: { from: location } });
      return;
    }

    addProductToCart(true);
  };

  const handleAddToCart = () => {
    addProductToCart(false);
  };

  const getWhatsAppMessage = (product) => {
    const stoneHindi = product.name;
    const stoneEnglish = product.name?.split("(")[0].trim();

    return `🔮 ${stoneEnglish} Consultation Request

  नमस्कार,
  मैं ${stoneHindi} रत्न धारण करने से पहले अपना उपयुक्त कैरेट वज़न और ज्योतिषीय सलाह लेना चाहता/चाहती हूं।

  🪔 कंसल्टेशन शुल्क: ₹${activePrice}

  📅 जन्म तिथि:
  ⏰ जन्म समय:
  📍 जन्म स्थान:

  धन्यवाद। 🙏`;
  };

  const handleTalkToAstrologer = (e) => {
    e.preventDefault();

    const isLoggedIn = localStorage.getItem("isLoggedIn") === "true";

    if (!isLoggedIn) {
      navigate("/login", { state: { from: location } });
      return;
    }

    const msg = encodeURIComponent(getWhatsAppMessage(product));

    window.open(
      `https://wa.me/919616339090?text=${msg}`,
      "_blank",
      "noopener,noreferrer",
    );
  };

  const handleChange = (e) => {
    let { name, value } = e.target;

    if (name === "phone") {
      value = value.replace(/\D/g, "").slice(0, 10);
    }

    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!form.name.trim()) newErrors.name = "Name is required";

    if (!form.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      newErrors.email = "Enter a valid email";
    }

    if (!form.phone.trim()) {
      newErrors.phone = "Phone number is required";
    } else if (!/^\d{10}$/.test(form.phone)) {
      newErrors.phone = "Enter a valid 10-digit phone number";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handlePayment = async () => {
    if (isOutOfStock) {
      alert("This selected option is currently out of stock");
      return;
    }

    if (!validateForm()) return;

    try {
      setIsPaying(true);

      const res = await fetch(`${BASE_URL}/api/generate-token`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          amount: activePrice,
          name: form.name,
          email: form.email,
          phone: form.phone,
          product_id: product.id,
          product_name: product.name,
          variant_id: selectedVariant?.id || null,
          type: "gemstone_recommendation",
        }),
      });

      const data = await res.json();

      if (!res.ok || !data.payment_link) {
        alert(data?.error || "Payment link error");
        setIsPaying(false);
        return;
      }

      window.location.href = data.payment_link;
    } catch (error) {
      console.error("Payment Error:", error);
      alert("Payment failed");
      setIsPaying(false);
    }
  };

  const suggestedProducts = relatedProducts
    .filter((item) => String(item.id) !== String(id))
    .filter((item) =>
      product?.category ? item.category === product.category : true,
    )
    .slice(0, 4);

  if (loading) return <p>Loading...</p>;

  if (!product) {
    return <p style={{ color: "red" }}>Product not found or failed to load.</p>;
  }

  return (
    <div className="ra-main-shop ra-shop-detail-wrapper">
      <div className="container">
        <button onClick={() => navigate(-1)} className="btn btn-secondary mb-3">
          ← Back
        </button>

        <div className="ra-flipkart-detail-card">
          <div className="row align-items-start">
            <div className="col-lg-5 col-md-6">
              <div className="ra-shop-detail-image-box">
                <div
                  className={
                    isOutOfStock
                      ? "ra-shop-stock-label out-stock"
                      : "ra-shop-stock-label"
                  }
                >
                  {isOutOfStock ? "Out of Stock" : "In Stock"}
                </div>

                <img
                  src={selectedImage || product.image}
                  alt={product.name}
                  className="ra-shop-detail-main-image"
                />
              </div>
            </div>

            <div className="col-lg-7 col-md-6">
              <div className="ra-shop-detail-main-text premium-detail-panel">
               

                <p className="ra-shop-detail-category">
                  <strong>Category:</strong> {product.category}
                </p>

                <div className="premium-selected-box">
                  

                 <div className="premium-selected-product-box">
  <h2>{product.name}</h2>

  <div className="premium-selected-size">
    {selectedVariant?.size || selectedVariant?.unit
      ? `${selectedVariant?.size || ""} ${selectedVariant?.unit || ""}`
      : "Selected Option"}
  </div>

  <div className="premium-selected-price-row">
    <h3>₹{activePrice.toLocaleString("en-IN")}</h3>
    {activeMainPrice > activePrice && (
      <del>₹{activeMainPrice.toLocaleString("en-IN")}</del>
    )}

    

    <span className={isOutOfStock ? "premium-stock out" : "premium-stock in"}>
      {isOutOfStock ? "Out of Stock" : "In Stock"}
    </span>
  </div>
</div>
                </div>

                {product.variants?.length > 0 && (
                  <div className="ra-premium-price-selector">
                    <div className="ra-price-selector-header">
                      <h4>Choose Option</h4>
                      <span>Size • Ratti/Carat • Price</span>
                    </div>

                    <div className="ra-premium-price-grid">
                      {product.variants.map((variant, index) => {
                        const variantOut =
                          variant.stock_status === "out_of_stock";
                        const isActive =
                          String(selectedVariant?.id) === String(variant.id);

                        const mainPrice = Number(variant.main_price || 0);
                        const sellPrice = Number(variant.price || 0);

                        const optionTitle =
                          variant.size && variant.unit
                            ? `${variant.size} ${variant.unit}`
                            : variant.size
                              ? variant.size
                              : variant.unit
                                ? variant.unit
                                : `Option ${index + 1}`;

                        const discount =
                          mainPrice > sellPrice
                            ? Math.round(
                                ((mainPrice - sellPrice) / mainPrice) * 100,
                              )
                            : 0;

                        return (
                          <button
                            type="button"
                            key={variant.id || index}
                            className={`ra-premium-price-card ${isActive ? "active" : ""} ${
                              variantOut ? "variant-out-stock" : ""
                            }`}
                            onClick={() => {
                              setSelectedVariant(variant);
                              setSelectedImage(
                                variant.imageUrl || product.image,
                              );
                            }}
                          >
                            <div className="variant-size-title">
                              {optionTitle}
                            </div>
                            <div className="variant-divider"></div>

                            {variantOut ? (
                              <div className="variant-out-text">
                                Out of Stock
                              </div>
                            ) : (
                              <>
                                <div className="variant-price-line">
                                  {discount > 0 && (
                                    <span className="variant-discount">
                                      ↓{discount}%
                                    </span>
                                  )}

                                  {mainPrice > 0 && (
                                    <del>
                                      ₹{mainPrice.toLocaleString("en-IN")}
                                    </del>
                                  )}
                                </div>

                                <strong>
                                  ₹{sellPrice.toLocaleString("en-IN")}
                                </strong>
                              </>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                <div className="ra-shop-short-info">
                  <span>✔ Certified Product</span>
                  <span>✔ Secure Packaging</span>
                  <span>✔ Trusted Astrology Store</span>
                </div>

                <div className="ra-shop-detail-action-row">
                  <button
                    className="ra-shop-detail-book-btn ra-shop-detail-action-btn"
                    onClick={handlePayClick}
                    disabled={isOutOfStock}
                    type="button"
                  >
                    {isOutOfStock ? "Out of Stock" : `Book Now ₹${activePrice}`}
                  </button>

                  <button
                    className="ra-shop-detail-cart-btn ra-shop-detail-action-btn"
                    onClick={handleAddToCart}
                    disabled={isOutOfStock}
                    type="button"
                  >
                    {isOutOfStock
                      ? "Out of Stock"
                      : `Add to Cart ₹${activePrice}`}
                  </button>
                </div>

                {cartMessage && (
                  <p className="ra-cart-message">✓ {cartMessage}</p>
                )}

                <div className="ra-shop-overview-box">
                  <h4>Product Overview</h4>
                  <p>{product.overview}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="ra-shop-detail-page mt-4">
          <div className="ra-shop-detail-description-card">
            <div
              className="ra-product-description"
              dangerouslySetInnerHTML={{
                __html: product.description || "",
              }}
            />
          </div>

          <div className="ra-shop-detail-static-section">
            <p>
              ✅ Free Shipping | ✅ COD Available | ✅ Returns Accepted | ✅
              Certified Stones
            </p>

            <p>
              📞 Don’t know your ideal carat size?
              <br />
              Talk to our Certified Astrologer — Consultation just ₹
              {activePrice}!
            </p>

            <button
              className="ra-shop-detail-astro-btn"
              onClick={handleTalkToAstrologer}
              type="button"
            >
              Talk to Astrologer
            </button>
          </div>
        </div>

        {suggestedProducts.length > 0 && (
          <div className="ra-suggested-products-section">
            <h3 className="ra-suggested-products-title">Suggested Products</h3>

            <div className="row">
              {suggestedProducts.map((item) => (
                <div className="col-lg-3 col-md-4 col-sm-6 mb-4" key={item.id}>
                  <div
                    className="ra-shop-product-card ra-suggested-product-card"
                    onClick={() => navigate(`/product/${item.id}`)}
                    style={{ cursor: "pointer" }}
                  >
                    <img src={item.image} alt={item.name} />

                    <div className="product-info">
                      <h4 className="ra-shop-product-name">{item.name}</h4>
                      <p className="ra-shop-price">₹{item.price}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductDetail;
